import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PeepingLogo } from "@/components/PeepingLogo";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const f = new FormData(e.currentTarget);
    const pw = String(f.get("password") || password);
    const cf = String(f.get("confirm") || confirm);
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== cf) return setError("Passwords do not match — tap the eye icon to check both fields.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-screen" className="relative min-h-screen bg-[#080808] grain">
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pb-10 pt-16">
        <div className="flex flex-col items-center">
          <PeepingLogo className="w-44" />
        </div>
        <div className="mt-10">
          <h1 className="text-2xl font-bold">{params.get("setup") ? "Create your reviewer password" : "Set a new password"}</h1>
          <p className="mt-1 text-sm text-[#a7a0af]">Choose a strong password you haven't used before.</p>
        </div>

        {!token ? (
          <p className="mt-8 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
            This reset link is missing its token. Please request a new link.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col gap-3" noValidate>
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
              <input
                data-testid="reset-password"
                name="password"
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password"
                className="field w-full rounded-2xl py-4 pl-11 pr-11 text-sm"
              />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a7a0af]">
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
              <input
                data-testid="reset-confirm"
                name="confirm"
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                className="field w-full rounded-2xl py-4 pl-11 pr-4 text-sm"
              />
            </div>
            {error && <p data-testid="reset-error" role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="btn-brand mt-2 w-full rounded-2xl py-4 text-base font-semibold text-white"
            >
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
        <Link to="/login" className="mt-6 text-center text-sm font-semibold text-[#d42be8]">
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
